import classnames from 'classnames'
import { FormikProvider, useFormik } from 'formik'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import useUpdateAsset from '../../../state/mutations/useUpdateAsset'
import useAssets from '../../../state/queries/useAssets'
import FormButtons from '../../basics/FormButtons/FormButtons'
import { TextInputFormik } from '../../basics/TextInput/TextInput'

import styles from './EditAssetForm.module.scss'

type EditAssetFormProps = {
  learningItemUuid: string
  assetUuid: string
  onClose: () => void
}

export default React.memo(function EditAssetForm({ learningItemUuid, assetUuid, onClose }: EditAssetFormProps) {
  const { t } = useTranslation()

  const { assets } = useAssets(learningItemUuid)
  const asset = useMemo(() => assets.find(({ uuid }) => uuid === assetUuid), [assetUuid, assets])

  const updateAsset = useUpdateAsset()
  const formik = useFormik({
    initialValues: {
      uuid: assetUuid,
      learningItemUuid,

      filename: asset?.filename || '',
      mimeType: asset?.mimeType || '',
      description: asset?.description || null,
      visibleFrom: asset?.visibleFrom || null,
      visibleUntil: asset?.visibleUntil || null,
    },
    enableReinitialize: true,
    onSubmit: async (values, { setStatus }) => {
      setStatus()
      try {
        await updateAsset.mutateAsync(values)
        onClose()
      } catch (err) {
        setStatus(String(err))
      }
    },
  })

  return (
    <FormikProvider value={formik}>
      <form noValidate onSubmit={formik.handleSubmit} className={classnames(styles.EditAssetForm)}>
        <header>
          <hgroup>
            <h1>{t('asset.title edit asset')}</h1>
          </hgroup>
        </header>

        <div>
          <TextInputFormik name="filename" label={t('asset.field filename')} required />

          <TextInputFormik name="description" label={t('asset.field description')} />

          <TextInputFormik name="visibleFrom" label={t('asset.field visibleFrom')} type="date" />

          <TextInputFormik name="visibleUntil" label={t('asset.field visibleUntil')} type="date" />
        </div>

        <FormButtons onClose={onClose} {...formik} isValid={true} />
      </form>
    </FormikProvider>
  )
})
